import { useSelector, shallowEqual } from 'react-redux';
import moment from 'moment';

import {
  selectTariffDetails,
  selectGasTariff,
  selectElectricityTariff
} from 'state/tariff-information/tariff-information-slice';
import { PAYMENT_NAMES } from 'config/app-config';

export const roundDecimal = (s) =>
  parseFloat(s).toFixed(3).replace(/\.0+$/, '');

export const formatDate = (d) => {
  const parsedDate = moment(d, 'YYYY-MM-DDThh:mm:ssZ');
  return parsedDate.isValid() ? parsedDate.format('DD MMMM YYYY') : d;
};

export const formatUnitRate = (s) => `${roundDecimal(s)}p per kWh`;
export const formatStandingCharge = (s) => `${roundDecimal(s)}p per day`;
export const formatConsumption = (s) => `${roundDecimal(s)} kWh`;
export const formatPrice = (s) =>
  `£${parseFloat(roundDecimal(s)).toLocaleString('en-US')}`;
const totalAnnualPrice = (gasTariff, electricityTariff) => {
  switch (true) {
    case !gasTariff:
      return electricityTariff.estimatedPersonalProjection;
    case !electricityTariff:
      return gasTariff.estimatedPersonalProjection;
    default:
      return (
        gasTariff.estimatedPersonalProjection +
        electricityTariff.estimatedPersonalProjection
      );
  }
};

const getElectricityDetails = (electricityTariff, exitFee) => {
  const hasElectricity = !!electricityTariff;

  return hasElectricity
    ? {
        electricity: {
          standingCharge: formatStandingCharge(
            electricityTariff.standingCharge
          ),
          ...(electricityTariff.unitRate && {
            unitRate: formatUnitRate(electricityTariff.unitRate)
          }),
          ...(electricityTariff.unitRateDay && {
            unitRateDay: formatUnitRate(electricityTariff.unitRateDay),
            unitRateDayName: electricityTariff.unitRateDayName
          }),
          ...(electricityTariff.unitRateNight && {
            unitRateNight: formatUnitRate(electricityTariff.unitRateNight),
            unitRateNightName: electricityTariff.unitRateNightName
          }),
          exitFee
        }
      }
    : {};
};

const getGasDetails = (gasTariff, exitFee) => {
  const hasGas = !!gasTariff;
  return hasGas
    ? {
        gas: {
          standingCharge: formatStandingCharge(gasTariff.standingCharge),
          unitRate: formatUnitRate(gasTariff.unitRate),
          exitFee
        }
      }
    : {};
};

const getAnnualProjectionDetails = ({ electricityTariff, gasTariff }) => {
  const hasGas = !!gasTariff;
  const hasElectricity = !!electricityTariff;

  return {
    ...(hasElectricity && {
      electricity: {
        consumption: formatConsumption(
          electricityTariff.estimatedAnnualConsumption
        ),
        price: formatPrice(electricityTariff.estimatedPersonalProjection)
      }
    }),
    ...(hasGas && {
      gas: {
        consumption: formatConsumption(gasTariff.estimatedAnnualConsumption),
        price: formatPrice(gasTariff.estimatedPersonalProjection)
      }
    }),
    total: {
      price: formatPrice(totalAnnualPrice(gasTariff, electricityTariff))
    }
  };
};

export const useTariffDetails = () => {
  const tariffDetails = useSelector(selectTariffDetails, shallowEqual);
  const gasTariff = useSelector(selectGasTariff, shallowEqual);
  const electricityTariff = useSelector(selectElectricityTariff, shallowEqual);

  if (!tariffDetails) {
    return null;
  }

  const {
    tariffName,
    tariffType,
    endDate,
    additionalProducts,
    exitFee,
    termsAndConditions,
    paymentType
  } = tariffDetails;

  const hasGas = !!gasTariff;
  const hasElectricity = !!electricityTariff;
  const energyType = [
    ...(hasGas ? ['Gas'] : []),
    ...(hasElectricity ? ['Electricity'] : [])
  ].join(' and ');
  const electricityDetails = getElectricityDetails(electricityTariff, exitFee);
  const gasDetails = getGasDetails(gasTariff, exitFee);
  const annualProjection = getAnnualProjectionDetails({
    gasTariff,
    electricityTariff
  });
  const tariffEndDate = formatDate(endDate);

  return {
    common: {
      tariffName,
      energyType,
      tariffType,
      paymentType: PAYMENT_NAMES[paymentType],
      endDate: tariffEndDate,
      additionalProducts,
      termsAndConditions,
      exitFee
    },
    ...electricityDetails,
    ...gasDetails,
    annualProjection
  };
};
